import { useState, useEffect } from "react";
import { Badge } from "@/components/ui/badge";
import { Clock, AlertTriangle } from "lucide-react";

interface CountdownTimerProps {
  initialMinutes?: number;
  onExpire?: () => void;
}

export function CountdownTimer({ initialMinutes = 15, onExpire }: CountdownTimerProps) {
  const [timeLeft, setTimeLeft] = useState(initialMinutes * 60);
  const [isExpired, setIsExpired] = useState(false);

  useEffect(() => {
    if (timeLeft <= 0) {
      setIsExpired(true);
      onExpire?.();
      return;
    }

    const timer = setInterval(() => {
      setTimeLeft((prev) => prev - 1);
    }, 1000);

    return () => clearInterval(timer);
  }, [timeLeft, onExpire]);

  const formatTime = (seconds: number) => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes.toString().padStart(2, '0')}:${remainingSeconds.toString().padStart(2, '0')}`;
  };

  const isUrgent = timeLeft <= 300; // Últimos 5 minutos
  const isCritical = timeLeft <= 60; // Último minuto

  if (isExpired) {
    return (
      <Badge variant="destructive" className="animate-pulse">
        <AlertTriangle className="w-4 h-4 mr-2" />
        Oferta Expirada
      </Badge>
    );
  }

  return (
    <div className={`inline-flex items-center gap-2 px-4 py-2 rounded-full border transition-all duration-300 ${
      isCritical 
        ? 'bg-destructive/20 border-destructive text-destructive animate-pulse' 
        : isUrgent 
        ? 'bg-orange-500/20 border-orange-500 text-orange-500' 
        : 'bg-primary/20 border-primary text-primary'
    }`}>
      <Clock className={`w-4 h-4 ${isCritical ? 'animate-bounce' : ''}`} />
      <span className="font-mono font-bold text-lg">
        {formatTime(timeLeft)}
      </span>
      <span className="text-sm font-medium">
        {isCritical ? 'ÚLTIMA CHANCE!' : isUrgent ? 'ÚLTIMOS MINUTOS!' : 'restantes'}
      </span>
    </div>
  );
}